'use client';

import { useQuery } from '@tanstack/react-query';
import { getCurrentGatewayUrl } from '../../utils/environmentDetection';
import { getAuthHeaders } from '../../utils/authHeaders';

export type ChatFilesystemFileMeta = {
  path: string;
  size: number;
  modifiedAt: string;
};

export type ChatFilesystemFile = {
  path: string;
  content: string;
  createdAt: string;
  modifiedAt: string;
};

export const CHAT_FILESYSTEM_QUERY_KEY = 'chat-filesystem';

const buildBaseUrl = (chatId: string): string =>
  `${getCurrentGatewayUrl()}/chat/graphs/co-pilot/${chatId}/filesystem`;

const withApplicationId = (
  url: string,
  applicationId: string | null | undefined,
): string => {
  if (!applicationId?.trim()) {
    return url;
  }

  const separator = url.includes('?') ? '&' : '?';

  return `${url}${separator}applicationId=${encodeURIComponent(applicationId.trim())}`;
};

export function useChatFilesystem(
  chatId: string,
  enabled = true,
  applicationId?: string | null,
) {
  return useQuery({
    enabled: enabled && Boolean(chatId),
    queryFn: async (): Promise<ChatFilesystemFileMeta[]> => {
      const authHeaders = await getAuthHeaders();
      const response = await fetch(
        withApplicationId(buildBaseUrl(chatId), applicationId),
        {
          credentials: 'include',
          headers: { ...authHeaders },
          method: 'GET',
        },
      );

      if (!response.ok) {
        throw new Error(`Failed to load filesystem (${response.status})`);
      }

      const body = (await response.json()) as {
        files?: ChatFilesystemFileMeta[];
      };

      return body.files ?? [];
    },
    queryKey: [CHAT_FILESYSTEM_QUERY_KEY, chatId, applicationId ?? null],
    staleTime: 5000,
  });
}

export function useChatFile(
  chatId: string,
  path: string | null,
  applicationId?: string | null,
) {
  return useQuery({
    enabled: Boolean(chatId) && Boolean(path),
    queryFn: async (): Promise<ChatFilesystemFile | null> => {
      if (!path) {
        return null;
      }

      const authHeaders = await getAuthHeaders();
      const url = withApplicationId(
        `${buildBaseUrl(chatId)}/file?path=${encodeURIComponent(path)}`,
        applicationId,
      );
      const response = await fetch(url, {
        credentials: 'include',
        headers: { ...authHeaders },
        method: 'GET',
      });

      if (response.status === 404) {
        return null;
      }

      if (!response.ok) {
        throw new Error(`Failed to load file (${response.status})`);
      }

      const body = (await response.json()) as { file?: ChatFilesystemFile };

      return body.file ?? null;
    },
    queryKey: [
      CHAT_FILESYSTEM_QUERY_KEY,
      chatId,
      'file',
      path,
      applicationId ?? null,
    ],
    staleTime: 5000,
  });
}
